/**
 * Riga della lista Instagram/Messenger, con lo stesso menu della lista WhatsApp
 * (tasto destro o pulsante "..."): segna come letta, fissa in alto, archivia,
 * silenzia. Le preferenze stanno in whatsapp_chat_meta con chiave 'meta:<id>'.
 */
import { useState } from 'react';
import { MoreHorizontal, Pin, BellOff, Instagram, Facebook } from 'lucide-react';
import type { MetaConversation } from '@/lib/stores/metaMessagingStore';
import { TimeAgo } from '@/components/whatsapp2/shared/TimeAgo';
import { tip } from '@/components/messages/ChatTooltip';
import { ChatAvatar } from '@/components/whatsapp2/chat/ChatAvatar';
import { ChatRowMenu } from '@/components/whatsapp2/sidebar/ChatRowMenu';
import type { MuteOption } from '@/lib/services/whatsapp/chatListRules';
import { needsReply, windowState } from './metaConversationHelpers';

interface Props {
  conversation: MetaConversation;
  label: string;
  active: boolean;
  unread: boolean;
  onSelect: (conversationId: string) => void;
  onMarkAsRead: () => void;
  prefs: { pinned: boolean; archived: boolean; muted: boolean };
  prefsAvailable: boolean;
  onTogglePin: () => void;
  onToggleArchive: () => void;
  onMute: (option: MuteOption | null) => void;
}

export function MetaConversationRow({ conversation, label, active, unread, onSelect, onMarkAsRead, prefs, prefsAvailable, onTogglePin, onToggleArchive, onMute }: Props) {
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const Icon = conversation.channel === 'instagram' ? Instagram : Facebook;
  const pending = needsReply(conversation);
  const blocked = windowState(conversation) === 'blocked';
  const dot = prefs.muted ? 'bg-[#9B9A97]' : 'bg-[#2383e2]';

  return (
    <div className="relative group">
      <button
        type="button"
        onClick={() => onSelect(conversation.id)}
        onContextMenu={(e) => { e.preventDefault(); setMenu({ x: e.clientX, y: e.clientY }); }}
        className={[
          'w-full text-left px-3 py-2.5 flex items-start gap-3 border-b border-[rgba(55,53,47,0.06)] dark:border-white/5 transition-colors animate-fade-in',
          active ? 'bg-[rgba(35,131,226,0.08)]' : 'hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5',
        ].join(' ')}
      >
        <div className="relative flex-shrink-0">
          <ChatAvatar name={label} imageUrl={conversation.profile_pic_url || undefined} size="md" />
          <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-white dark:bg-[#191919] flex items-center justify-center" aria-hidden>
            <Icon className="h-3 w-3 text-[#787774]" />
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className={`text-sm truncate ${unread ? 'font-semibold text-[#37352F] dark:text-white' : 'font-medium text-[#37352F] dark:text-gray-200'}`}>
              {label}
            </span>
            <span className="flex items-center gap-1 flex-shrink-0">
              {prefs.muted && <BellOff className="w-3 h-3 text-[#9B9A97]" aria-label="Silenziata" />}
              {prefs.pinned && <Pin className="w-3 h-3 text-[#9B9A97]" aria-label="Fissata in alto" />}
              <TimeAgo timestamp={conversation.last_message_at} hasUnread={unread && !prefs.muted} />
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {unread && <span className={`h-2 w-2 rounded-full ${dot} flex-shrink-0`} aria-label="Non letto" />}
            <span className={`text-xs truncate flex-1 min-w-0 ${unread ? 'text-[#37352F] dark:text-gray-200' : 'text-[#787774]'}`}>
              {conversation.last_message_preview
                ? `${conversation.last_message_direction === 'outbound' ? 'Tu: ' : ''}${conversation.last_message_preview}`
                : pending ? 'In attesa di risposta' : 'Nessun messaggio'}
            </span>
            {blocked ? (
              <span
                className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-[rgba(55,53,47,0.06)] text-[#787774]"
                {...tip('Sono passati i giorni in cui Meta permette di rispondere: il contatto deve riscriverti')}
              >
                Chiusa
              </span>
            ) : pending ? (
              <span
                className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400"
                {...tip('L\'ultimo messaggio e\' del contatto: aspetta una risposta')}
              >
                Da rispondere
              </span>
            ) : null}
          </div>
        </div>
      </button>

      <button
        type="button"
        aria-label="Altre azioni sulla conversazione"
        {...tip('Fissa, archivia, silenzia')}
        onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setMenu({ x: r.right - 210, y: r.bottom + 4 }); }}
        className={`absolute right-2 bottom-2 p-1 rounded-full bg-white dark:bg-gray-800 shadow-sm border border-[rgba(55,53,47,0.09)] dark:border-white/10 text-[#787774] hover:text-[#37352F] dark:hover:text-white transition-opacity ${menu ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}`}
      >
        <MoreHorizontal className="w-3.5 h-3.5" />
      </button>

      {menu && (
        <ChatRowMenu
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          // Instagram/Messenger: si puo' segnare come letta; "non letta" non esiste
          // ancora (le letture sono per utente e contano solo in avanti).
          hasUnread={unread ? true : undefined}
          onMarkAsRead={onMarkAsRead}
          pinned={prefs.pinned}
          archived={prefs.archived}
          muted={prefs.muted}
          prefsAvailable={prefsAvailable}
          onTogglePin={onTogglePin}
          onToggleArchive={onToggleArchive}
          onMute={onMute}
        />
      )}
    </div>
  );
}
